// Goals & Portfolios — OKR hierarchy (status / progress roll-up) on the left,
// portfolios (project groupings) on the right. Mirrors the /api/goals and
// /api/portfolios routes in workers/ac-edge/src/index.ts.
//
// NOTE: the API has no endpoint to LIST a portfolio's projects (only POST
// /api/portfolios/:id/projects to add one), so the expanded portfolio shows the
// add-project picker plus the projects added during this session — never faked
// counts. Flagged for the worker team.

import { useMemo, useState, type FormEvent } from "react";
import {
  createGoal,
  deleteGoal,
  listGoals,
  rollupGoal,
  updateGoal,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort } from "../lib/format";
import type { GoalRow, GoalStatus } from "../lib/types";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { confirmDialog } from "../components/ConfirmDialog";

/** The goal status enum is part of the wire contract (goals-db), not tenant config. */
const GOAL_STATUSES: { value: GoalStatus; label: string }[] = [
  { value: "on_track", label: "On track" },
  { value: "at_risk", label: "At risk" },
  { value: "off_track", label: "Off track" },
  { value: "done", label: "Done" },
];

/** Parse a yyyy-mm-dd input into unix seconds (local midnight); null when empty. */
function dateInputToUnix(value: string): number | null {
  if (!value) return null;
  const ms = new Date(`${value}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

/** Flatten the goal tree into render order; children indent one level. */
function orderGoals(goals: GoalRow[]): { goal: GoalRow; depth: number }[] {
  const ids = new Set(goals.map((g) => g.id));
  const children = new Map<string, GoalRow[]>();
  const roots: GoalRow[] = [];
  for (const g of goals) {
    if (g.parent_id && ids.has(g.parent_id)) {
      const list = children.get(g.parent_id) ?? [];
      list.push(g);
      children.set(g.parent_id, list);
    } else {
      roots.push(g);
    }
  }
  const out: { goal: GoalRow; depth: number }[] = [];
  const seen = new Set<string>();
  function walk(g: GoalRow, depth: number) {
    if (seen.has(g.id)) return; // cycle guard
    seen.add(g.id);
    out.push({ goal: g, depth: Math.min(depth, 1) });
    for (const c of children.get(g.id) ?? []) walk(c, depth + 1);
  }
  for (const r of roots) walk(r, 0);
  return out;
}

export function GoalsPage() {
  return (
    <>
      <PageBar crumbs="Goals" />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Goals</h1>
            <div className="sub">Objectives rolled up from linked work.</div>
          </div>
        </div>
        <GoalsCard />
      </div>
    </>
  );
}

// ===========================================================================
// Goals (left column)
// ===========================================================================

function GoalsCard() {
  const { data, loading, error, reload } = useApi((signal) => listGoals(signal), []);
  const goals = data?.goals ?? [];
  const ordered = useMemo(() => orderGoals(goals), [goals]);
  const [creating, setCreating] = useState(false);

  return (
    <section className="card" aria-label="Goals">
      <header className="card-head">
        <h2>Goals</h2>
        <button className="btn small primary" onClick={() => setCreating(true)}>
          + New goal
        </button>
      </header>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading goals…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : ordered.length === 0 ? (
          <EmptyState>No goals yet — create one with “New goal”.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Goal</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Due</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {ordered.map(({ goal, depth }) => (
                <GoalTableRow key={goal.id} goal={goal} depth={depth} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating ? (
        <CreateGoalModal
          goals={goals}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function GoalTableRow({
  goal,
  depth,
  onChanged,
}: {
  goal: GoalRow;
  depth: number;
  onChanged: () => void;
}) {
  const updateMut = useMutation(updateGoal);
  const rollupMut = useMutation(rollupGoal);
  const deleteMut = useMutation(deleteGoal);
  const busy = updateMut.pending || rollupMut.pending || deleteMut.pending;
  const rowError = updateMut.error ?? rollupMut.error ?? deleteMut.error;
  const pct = Math.round(Math.max(0, Math.min(1, goal.progress)) * 100);

  async function onStatus(status: GoalStatus) {
    const res = await updateMut.run(goal.id, { status });
    if (res) onChanged();
  }

  async function onRollup() {
    const res = await rollupMut.run(goal.id);
    if (res) onChanged();
  }

  async function onDelete() {
    if (!(await confirmDialog({ message: `Delete goal "${goal.name}"? Sub-goals and links are removed too.`, danger: true }))) return;
    const res = await deleteMut.run(goal.id);
    if (res) onChanged();
  }

  return (
    <>
      <tr>
        <td>
          <span style={depth > 0 ? { paddingLeft: 18, display: "inline-block" } : undefined}>
            {depth > 0 ? <span className="faint" aria-hidden="true">↳ </span> : null}
            <strong>{goal.name}</strong>
          </span>
          {goal.description ? <div className="sub">{goal.description}</div> : null}
        </td>
        <td>
          <label className="sr-only" htmlFor={`goal-status-${goal.id}`}>
            Status of {goal.name}
          </label>
          <select
            id={`goal-status-${goal.id}`}
            value={goal.status}
            disabled={busy}
            onChange={(e) => onStatus(e.target.value as GoalStatus)}
          >
            {GOAL_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </td>
        <td>
          <div className="bar-track" style={{ minWidth: 90 }} title={`${pct}% complete`}>
            <div className="bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="sub num">{pct}%</div>
        </td>
        <td className="num">{formatDateShort(goal.due_date)}</td>
        <td className="row-actions">
          <button
            className="btn small"
            disabled={busy}
            onClick={onRollup}
            title="Recompute progress from linked work"
          >
            {rollupMut.pending ? <Spinner /> : "Rollup"}
          </button>
          <button className="btn small danger" disabled={busy} onClick={onDelete}>
            Delete
          </button>
        </td>
      </tr>
      {rowError ? (
        <tr>
          <td colSpan={5}>
            <Alert kind="error">{rowError}</Alert>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function CreateGoalModal({
  goals,
  onClose,
  onCreated,
}: {
  goals: GoalRow[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const createMut = useMutation(createGoal);
  const patchMut = useMutation(updateGoal);
  const pending = createMut.pending || patchMut.pending;
  const error = createMut.error ?? patchMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = await createMut.run({
      name: name.trim(),
      ...(parentId ? { parent_id: parentId } : {}),
    });
    if (!res) return;
    // The create route only accepts name/description/parent_id; the due date is
    // applied with a follow-up PATCH. A PATCH failure still leaves the goal made.
    const due = dateInputToUnix(dueDate);
    if (due != null) await patchMut.run(res.goal.id, { due_date: due });
    onCreated();
  }

  return (
    <Modal
      title="New goal"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="create-goal-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Creating…" /> : "Create goal"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-goal-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Goal</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cut first-response time in half"
            autoFocus
            required
          />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Parent (optional)</span>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— top level —</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Due date (optional)</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
        </div>
      </form>
    </Modal>
  );
}
